import { motion } from "framer-motion";
import { Check, Circle } from "lucide-react";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { PageTour } from "@/components/tour/PageTour";
import { onboardingProcessSteps } from "@/lib/tour-steps";

const onboardingSteps = [
  {
    number: 1,
    title: "Initial Contact",
    description: "First conversation to understand client needs and goals",
    details: ["Discovery call scheduling", "Basic company info collection", "Pain point identification"],
  },
  {
    number: 2,
    title: "Discovery Call",
    description: "Deep dive into client's business and demo requirements",
    details: ["ICP definition", "Target market analysis", "Budget discussion", "Timeline alignment"],
  },
  {
    number: 3,
    title: "Package Selection",
    description: "Choose between Standard and Elite based on needs",
    details: ["Feature comparison", "Pricing review", "Contract signing", "Payment setup"],
  },
  {
    number: 4,
    title: "Setup & Integration",
    description: "Connect all necessary platforms and tools",
    details: ["GoHighLevel CRM setup", "Ad account access", "Calendar integration", "Slack channel creation"],
  },
  {
    number: 5,
    title: "Creative Production",
    description: "Design and produce ad creatives and copy",
    details: ["Brand asset review", "Ad copy development", "Visual creative design", "Landing page setup"],
  },
  {
    number: 6,
    title: "Campaign Launch",
    description: "Go live with paid advertising campaigns",
    details: ["Campaign structure setup", "Audience targeting", "Budget allocation", "Tracking implementation"],
  },
  {
    number: 7,
    title: "First Demo Delivery",
    description: "Begin delivering qualified demos to client",
    details: ["Demo scheduling", "Call team briefing", "Quality assurance", "Client feedback loop"],
  },
];

export default function OnboardingProcess() {
  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <PageTour steps={onboardingProcessSteps} />
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold">Onboarding Process</h1>
        <p className="text-muted-foreground">Step-by-step client onboarding workflow</p>
      </div>

      {/* Timeline */}
      <VentureCard>
        <VentureCardContent className="py-8">
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-secondary to-primary" />
            
            <div className="space-y-8">
              {onboardingSteps.map((step, index) => (
                <motion.div
                  key={step.number}
                  className="relative flex gap-6"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {/* Step number circle */}
                  <div className="relative z-10 w-12 h-12 rounded-full bg-surface border-2 border-secondary flex items-center justify-center flex-shrink-0">
                    <span className="text-lg font-bold text-secondary">{step.number}</span>
                  </div>
                  
                  {/* Step content */}
                  <div className="flex-1 pb-8">
                    <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                    <p className="text-muted-foreground mb-4">{step.description}</p>
                    
                    <div className="bg-surface rounded-lg p-4 space-y-2">
                      {step.details.map((detail, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <Check className="w-4 h-4 text-secondary flex-shrink-0" />
                          <span className="text-sm">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </VentureCardContent>
      </VentureCard>
    </motion.div>
  );
}
